import { createFileRoute, useNavigate, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MapPin, Save, Building2, Trash2, Loader2, RotateCcw, Radio as RadioIcon } from "lucide-react";
import { toast } from "sonner";
import { GeofenceWizardMap, AddressSearch } from "@/components/GeofenceWizard";

export const Route = createFileRoute("/_authenticated/sites/$siteId/edit")({
  component: EditSitePage,
  ssr: false,
  head: () => ({ meta: [{ title: "Edit site — MissionReady" }] }),
});

function StepBadge({ n }: { n: number }) {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-[11px] font-bold text-background">
      {n}
    </span>
  );
}

function EditSitePage() {
  const { siteId } = useParams({ from: "/_authenticated/sites/$siteId/edit" });
  const { currentOrg, currentRole } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const isAdmin = ["org_owner", "admin"].includes(currentRole ?? "");
  const [radioLiveKit, setRadioLiveKit] = useState<boolean | null>(null);
  const [radioSaving, setRadioSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [radius, setRadius] = useState(150);
  const [geofenceId, setGeofenceId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: site, error } = await supabase
        .from("sites")
        .select("id,name,address,latitude,longitude,organization_id,radio_use_livekit")
        .eq("id", siteId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !site) {
        toast.error(error?.message ?? "Site not found");
        navigate({ to: "/sites" });
        return;
      }
      setName(site.name ?? "");
      setAddress(site.address ?? "");
      setRadioLiveKit(site.radio_use_livekit ?? true);
      if (site.latitude != null && site.longitude != null) {
        setCenter({ lat: Number(site.latitude), lng: Number(site.longitude) });
      }
      const { data: gf } = await supabase
        .from("geofences")
        .select("id,center_lat,center_lng,radius_m")
        .eq("site_id", siteId)
        .eq("kind", "inclusion")
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (gf) {
        setGeofenceId(gf.id);
        setRadius(Math.round(Number(gf.radius_m ?? 150)));
        if (gf.center_lat != null && gf.center_lng != null) {
          setCenter({ lat: Number(gf.center_lat), lng: Number(gf.center_lng) });
        }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [currentOrg?.id, siteId, navigate]);

  if (!canEdit) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground">
        You don't have permission to edit sites.
      </div>
    );
  }

  const setRadioTransport = async (useLiveKit: boolean) => {
    if (radioLiveKit === useLiveKit) return;
    const prev = radioLiveKit;
    setRadioLiveKit(useLiveKit);
    setRadioSaving(true);
    const { error } = await supabase
      .from("sites")
      .update({ radio_use_livekit: useLiveKit })
      .eq("id", siteId);
    setRadioSaving(false);
    if (error) {
      setRadioLiveKit(prev);
      return toast.error(error.message);
    }
    toast.success(`Radio connection set to ${useLiveKit ? "LiveKit" : "Legacy"}`);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give the site a name");
    if (!center) return toast.error("Drop a pin first");
    setBusy(true);
    const { error: siteErr } = await supabase
      .from("sites")
      .update({
        name: name.trim(),
        address: address || null,
        latitude: center.lat,
        longitude: center.lng,
      })
      .eq("id", siteId);
    if (siteErr) { setBusy(false); return toast.error(siteErr.message); }

    if (geofenceId) {
      const { error: gErr } = await supabase
        .from("geofences")
        .update({
          center_lat: center.lat,
          center_lng: center.lng,
          radius_m: Math.round(radius),
          name: `${name.trim()} perimeter`,
        })
        .eq("id", geofenceId);
      if (gErr) { setBusy(false); return toast.error(gErr.message); }
    } else if (currentOrg) {
      const { data: created, error: gErr } = await supabase.from("geofences").insert({
        organization_id: currentOrg.id,
        site_id: siteId,
        name: `${name.trim()} perimeter`,
        kind: "inclusion",
        center_lat: center.lat,
        center_lng: center.lng,
        radius_m: Math.round(radius),
        active: true,
      }).select("id").single();
      if (gErr) { setBusy(false); return toast.error(gErr.message); }
      if (created) setGeofenceId(created.id);
    }
    setBusy(false);
    toast.success("Site updated");
    navigate({ to: "/sites" });
  };

  const remove = async () => {
    if (!confirm("Delete this site and its perimeter? This cannot be undone.")) return;
    setDeleting(true);
    await supabase.from("geofences").delete().eq("site_id", siteId);
    const { error } = await supabase.from("sites").delete().eq("id", siteId);
    setDeleting(false);
    if (error) return toast.error(error.message);
    toast.success("Site deleted");
    navigate({ to: "/sites" });
  };

  const removeGeofenceOnly = async () => {
    if (!geofenceId) return toast.info("No geofence to delete on this site");
    if (!confirm("Delete the perimeter (geofence) for this site? The site itself will remain.")) return;
    const { error } = await supabase.from("geofences").delete().eq("id", geofenceId);
    if (error) return toast.error(error.message);
    setGeofenceId(null);
    toast.success("Geofence deleted");
  };

  const resetGeofence = async () => {
    if (!confirm("Clear the current perimeter and start a fresh 3-step setup?")) return;
    if (geofenceId) {
      const { error } = await supabase.from("geofences").delete().eq("id", geofenceId);
      if (error) return toast.error(error.message);
    }
    setGeofenceId(null);
    setCenter(null);
    setRadius(150);
    setAddress("");
    toast.success("Geofence cleared — set a new one above");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-dvh bg-muted/40">
      <header className="sticky top-0 z-10 bg-foreground text-background">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          <Link to="/sites" className="rounded-md p-1 hover:bg-background/10" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <MapPin className="h-5 w-5" />
          <h1 className="font-display text-xl font-bold">Edit Site &amp; Geofence</h1>
        </div>
        <p className="mx-auto max-w-3xl px-4 pb-4 text-sm text-background/70">
          Update the site name, move the pin, or resize the perimeter.
        </p>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading site…
        </div>
      ) : (
        <div className="mx-auto max-w-3xl space-y-4 px-4 py-5">
          <section className="rounded-2xl border border-border bg-card p-4">
            <Label htmlFor="site-name" className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" /> Site
            </Label>
            <Input
              id="site-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Main Campus — Building A"
              className="h-11 text-base"
            />
          </section>

          {isAdmin && radioLiveKit !== null && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <RadioIcon className="h-3.5 w-3.5" /> Radio connection
              </div>
              <p className="mb-3 text-sm text-muted-foreground">
                Takes effect the next time Radio connects on this site.
              </p>
              <div role="radiogroup" aria-label="Radio connection" className="grid grid-cols-2 gap-2">
                {[
                  { value: true, label: "LiveKit (recommended)" },
                  { value: false, label: "Legacy" },
                ].map((opt) => (
                  <Button
                    key={opt.label}
                    type="button"
                    role="radio"
                    aria-checked={radioLiveKit === opt.value}
                    variant={radioLiveKit === opt.value ? "default" : "outline"}
                    disabled={radioSaving}
                    onClick={() => setRadioTransport(opt.value)}
                    className="h-11"
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <StepBadge n={1} />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Address search</span>
            </div>
            <AddressSearch
              onPick={(lat, lng, label) => {
                setCenter({ lat, lng });
                setAddress(label);
              }}
            />
            {address && (
              <p className="mt-3 text-sm text-muted-foreground">
                Current address: <span className="text-foreground">{address}</span>
              </p>
            )}
          </section>

          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <StepBadge n={2} />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Map editor <span className="font-normal normal-case text-muted-foreground/80">— tap or drag pin to move</span>
              </span>
            </div>
            <GeofenceWizardMap
              center={center}
              radius={radius}
              onChange={({ lat, lng, radius: r }) => { setCenter({ lat, lng }); setRadius(r); }}
              height={420}
            />
          </section>

          <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <StepBadge n={3} />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Save changes</span>
            </div>
            <Button
              type="button"
              onClick={save}
              disabled={busy || !center || !name.trim()}
              className="h-12 w-full gap-2 text-base"
            >
              <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={resetGeofence}
              className="h-11 w-full gap-2"
            >
              <RotateCcw className="h-4 w-4" /> Reset geofence (start over)
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={removeGeofenceOnly}
              disabled={!geofenceId}
              className="h-11 w-full gap-2"
            >
              <Trash2 className="h-4 w-4" /> Delete geofence only
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={remove}
              disabled={deleting}
              className="h-11 w-full gap-2 text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" /> {deleting ? "Deleting…" : "Delete site & geofence"}
            </Button>
          </section>
        </div>
      )}
    </div>
  );
}
